/**
 * Rendering one image through a detached `<webout-player>` (DEV-655).
 *
 * The editor has no image viewer: the centre player is mounted on *chapters*
 * (`chapters-to-mount.ts`), so selecting an image only opens a form. That is
 * the literal half of the ticket — "v 9:16 projektu si ho nedokažem zobraziť".
 * The other half, an image being drawn on the project's canvas instead of its
 * own, is DEV-697 Phase 2's per-image render size; this module is what makes
 * that override visible.
 *
 * The player exposes `generateImage(imageSlug, playIcon)`, which mounts an
 * *image project*, renders one frame and resolves to a `data:image/jpeg`
 * URL from `canvas.toDataURL()`. It does **not** need `init()`. The player's
 * own `DownloadImage` plugin is the blueprint — with two deliberate departures,
 * both recorded below.
 *
 * ## `generateImage()` can hang forever, so every call needs a deadline
 *
 * The promise it returns settles only from `app.onImageGenerated`. Nothing
 * rejects it: `generateImage()` calls `app.initImage()` **without awaiting it**
 * and without a catch, so every failure inside becomes an unhandled rejection
 * and the caller waits forever. Verified against the pinned 1.6.214 bundle,
 * not only the 1.6.208 checkout. Three reachable failures:
 *
 * 1. **No `dimensions` on the ref.** `prepareImageProject()` assigns
 *    `project.dimensions = imageRef.dimensions`, and `calculateSize()` then
 *    reads `project.origCoordsWidth / project.dimensions.width` — a TypeError
 *    on `undefined`. This is AC 4 of DEV-697, still open upstream.
 * 2. **A chapter-level image.** `prepareImageProject()` looks the slug up in
 *    `project.images` and *never* in `chapters[].images`; a miss returns
 *    `undefined` and `initImage()` bails without ever emitting. The platform's
 *    `GET /:slug/project.json` returns the stored content verbatim and flattens
 *    nothing, so chapter images are genuinely unreachable this way.
 * 3. **A width with no generated file.** `dimensions.width` picks the
 *    resolution-suffixed jpg; a missing one 404s inside `prepareImages()`.
 *
 * (1) and (2) are knowable before we start — {@link imageGenerateBlocker}
 * refuses those up front, so the operator gets a sentence instead of a spinner.
 * (3) is only knowable by trying, which is what the timeout is for.
 *
 * ## Departures from the `DownloadImage` blueprint
 *
 * - **Never call `destroy()` before `remove()`.** The plugin does; in the
 *   editor that breaks the *centre* player, because destroying corrupts
 *   module-level state shared by every instance in the page (see
 *   `player-teardown.ts`). `remove()` alone is enough — the element's
 *   `disconnectedCallback()` calls `destroy()` itself.
 * - **Tear down on failure too.** The plugin only tears down after a successful
 *   await, so a hang leaks a live `<webout-player>`. Here teardown is in a
 *   `finally`.
 */

import type { ImageRefDimensions } from './image-ref-dimensions';

/**
 * How long to wait for a render before giving up.
 *
 * Generous, because the player fetches project.json, image.json and the jpg
 * before it draws anything, and a cold cache makes that several seconds. The
 * cost of waiting too long is an operator staring at a spinner; the cost of
 * giving up too early is calling a working render broken.
 */
export const IMAGE_GENERATE_TIMEOUT_MS = 20_000;

/** Distinguishes "the player never answered" from a real error it reported. */
export class ImageGenerateTimeoutError extends Error {
	constructor(message = 'The player did not finish rendering in time.') {
		super(message);
		this.name = 'ImageGenerateTimeoutError';
	}
}

/**
 * The detached player, reduced to what the orchestration needs — so the
 * sequencing can be driven by a fake under `node --test`, the way
 * `player-seek.ts` is.
 */
export interface ImageGenerateTarget {
	/**
	 * Resolves once the element has rendered its shadow DOM (`playerReady`).
	 *
	 * Implementations must subscribe to the event **before** the element is
	 * appended: Lit fires `firstUpdated` on a microtask after connection, and a
	 * listener attached later can miss it and wait for an event that never
	 * comes again.
	 */
	whenReady(): Promise<void>;
	/** `generateImage(slug)`. May never settle — see the module comment. */
	generate(): Promise<string>;
	/** Called exactly once, on success, failure and timeout alike. Must not throw. */
	teardown(): void;
}

/**
 * Render one image, or fail within `timeoutMs`.
 *
 * Resolves to the `data:image/jpeg;base64,…` URL the player produced.
 */
export async function generateImagePreview(
	target: ImageGenerateTarget,
	timeoutMs: number = IMAGE_GENERATE_TIMEOUT_MS,
): Promise<string> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	const deadline = new Promise<never>((_, reject) => {
		timer = setTimeout(() => reject(new ImageGenerateTimeoutError()), timeoutMs);
	});

	const work = (async () => {
		await target.whenReady();
		return target.generate();
	})();

	try {
		// `Promise.race` attaches handlers to both, so whichever loses cannot
		// later surface as an unhandled rejection — which matters here, because
		// the losing side is routinely the player's own never-settling promise.
		return await Promise.race([work, deadline]);
	} finally {
		clearTimeout(timer);
		target.teardown();
	}
}

/** Where an image lives, read off the editor's file path for it. */
export type ImageScope = 'project' | 'chapter' | null;

const PROJECT_IMAGE_PATH = /^\/projects\/[^/]+\/images\/[^/]+\/image\.json$/;
const CHAPTER_IMAGE_PATH = /^\/projects\/[^/]+\/chapters\/[^/]+\/images\/[^/]+\/image\.json$/;

/**
 * Project-level or chapter-level, from the path the editor selects with.
 *
 * The two shapes are the platform's own routes (`player.routes.ts`), and they
 * are what decides whether the player can reach the image at all.
 */
export function imageScopeFromPath(path: string | null | undefined): ImageScope {
	if (!path) return null;
	if (CHAPTER_IMAGE_PATH.test(path)) return 'chapter';
	if (PROJECT_IMAGE_PATH.test(path)) return 'project';
	return null;
}

/**
 * Why this image cannot be rendered, or `null` when it can.
 *
 * Every branch is a case where `generateImage()` would hang rather than fail,
 * so refusing early is the difference between an explanation and a spinner.
 */
export function imageGenerateBlocker(
	path: string | null | undefined,
	ref: ImageRefDimensions | null | undefined,
): string | null {
	if (!ref) {
		return 'This image has no reference in project.json, so the player cannot reach it.';
	}

	const scope = imageScopeFromPath(path);
	if (scope === 'chapter') {
		return 'Only a project-level image can be rendered on its own: the player looks the slug up in the project\'s images and never inside a chapter. Copy the image to Project level to preview it.';
	}
	if (scope !== 'project') {
		return 'This image has no project-level path, so the player cannot reach it.';
	}

	const size = ref.dimensions;
	if (!size || !(size.width > 0) || !(size.height > 0)) {
		return 'Set a width and height under Dimensions first — the render reads them to size the canvas and never finishes without them.';
	}

	return null;
}

/**
 * What to say when the render ran out of time.
 *
 * Names the cause rather than reporting a bare timeout, because the two likely
 * ones are far apart: a ref the form should have blocked, or media that was
 * never generated at the width the ref asks for.
 */
export function imageGenerateTimeoutMessage(ref: ImageRefDimensions | null | undefined): string {
	const size = ref?.dimensions;
	if (!size || !(size.width > 0) || !(size.height > 0)) {
		return 'Rendering timed out. This image\'s reference has no render size, and the player never finishes without one — set a width and height under Dimensions.';
	}
	return `Rendering timed out. The player asks for the ${size.width} px version of this image's media by name; check that version was generated.`;
}

/**
 * `aspect-ratio` for the preview frame, so the dialog reserves the right shape
 * while the render is still running instead of jumping when the image lands.
 */
export function previewAspectRatio(ref: ImageRefDimensions | null | undefined): string | null {
	const size = ref?.dimensions;
	if (!size || !(size.width > 0) || !(size.height > 0)) return null;
	return `${size.width} / ${size.height}`;
}

/** Filename for the downloaded render. Mirrors the player's `<name>.jpg`. */
export function imageDownloadName(projectSlug: string, imageSlug: string): string {
	const parts = [projectSlug, imageSlug].map(p => p.trim()).filter(Boolean);
	return `${parts.join('-') || 'image'}.jpg`;
}

/**
 * `data:` URL → `Blob`, for the download.
 *
 * A data URL can be handed to `<a download>` directly, but a blob URL keeps the
 * href short and is revocable — the render of a 1920 px image is a megabyte of
 * base64 and browsers differ on how much of that they will accept in an
 * attribute. Same conversion as the player's own `base64ToBlob()`.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
	const comma = dataUrl.indexOf(',');
	if (!dataUrl.startsWith('data:') || comma < 0) {
		throw new Error('Not a data URL');
	}
	const header = dataUrl.slice(5, comma);
	if (!header.endsWith(';base64')) {
		throw new Error('Not a base64 data URL');
	}
	const type = header.slice(0, -';base64'.length) || 'application/octet-stream';
	const binary = atob(dataUrl.slice(comma + 1));
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return new Blob([bytes], { type });
}
